import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, Ticket } from "lucide-react";
import api from "@/api";
import ChapterHeading from "@/components/home/ChapterHeading";

const filters = [
  { label: "All", value: "All" },
  { label: "Tonight", value: "Tonight" },
  { label: "This Weekend", value: "Weekend" },
  { label: "Live Music", value: "Live" },
  { label: "Clubs", value: "Club" },
  { label: "Food & Markets", value: "Food" },
  { label: "Art", value: "Art" },
];

const statusStyle = {
  "Selling Fast": "border-neon-magenta/50 bg-neon-magenta/10 text-neon-magenta",
  "Open": "border-neon-lime/50 bg-neon-lime/10 text-neon-lime",
  "Guestlist Only": "border-neon-purple/50 bg-neon-purple/10 text-neon-purple",
};

function formatDate(iso) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
}

export default function EventsSection({ cityId, filter, setFilter }) {
  const [events, setEvents] = useState([]);

  const fetchEvents = () => {
    api.get(`/events?city=${cityId}`).then(({ data }) => setEvents(data)).catch(() => {});
  };

  useEffect(() => {
    fetchEvents();
  }, [cityId]);

  const visible = useMemo(() => {
    if (filter === "All") return events;
    if (filter === "Tonight") return events.filter((e) => e.date === new Date().toLocaleDateString("en-CA"));
    if (filter === "Weekend")
      return events.filter((e) => {
        const d = new Date(`${e.date}T12:00:00`).getDay();
        return d === 5 || d === 6 || d === 0;
      });
    return events.filter((e) => e.category === filter);
  }, [events, filter]);

  return (
    <section id="events" className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="events-section">
      <ChapterHeading
        num="01 // AFTER DARK EVENTS"
        title="Tonight's Lineup"
        sub="Warehouse raves, rooftop sets, night markets and gallery afterhours — every listing verified, every night mapped."
      />

      <div className="mb-10 flex flex-wrap gap-2" data-testid="events-filter-bar">
        {filters.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            data-testid={`filter-${f.value.toLowerCase()}`}
            className={`rounded-full px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-all hover:-translate-y-0.5 ${
              filter === f.value
                ? "bg-neon-lime font-bold text-black shadow-[0_0_20px_rgba(34,197,94,0.4)]"
                : "border border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((e, i) => (
          <motion.article
            key={e.id}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="group overflow-hidden rounded-2xl border border-white/10 bg-[#131722] transition-all hover:-translate-y-1.5 hover:border-neon-lime/40 hover:shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
            data-testid={`event-card-${e.id}`}
          >
            <div className="relative h-52 overflow-hidden">
              <img src={e.image} alt={e.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#131722] via-transparent to-transparent" />
              <span className={`absolute left-4 top-4 rounded-full border px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur-md ${statusStyle[e.status] || "border-white/30 bg-black/50 text-white"}`}>
                {e.status}
              </span>
              <span className="absolute right-4 top-4 rounded-full border border-white/20 bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur-md">
                {e.price}
              </span>
              {e.recurring && (
                <span className="absolute bottom-4 left-4 rounded-full border border-neon-cyan/50 bg-black/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neon-cyan backdrop-blur-md" data-testid={`event-weekly-badge-${e.id}`}>
                  Weekly
                </span>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-neon-cyan">
                <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{formatDate(e.date)} — {e.time}</span>
              </div>
              <h3 className="mt-3 font-display text-xl font-bold uppercase leading-tight tracking-tight">{e.name}</h3>
              <p className="mt-2 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                <MapPin className="h-3.5 w-3.5" />{e.venue} — {e.neighborhood}
              </p>
              <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-zinc-400">{e.description}</p>
              {e.external_link && (
                <a
                  href={e.external_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`event-tickets-link-${e.id}`}
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-neon-cyan/50 px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-neon-cyan transition-all hover:-translate-y-0.5 hover:bg-neon-cyan hover:text-black"
                >
                  <Ticket className="h-4 w-4" /> Get Tickets ↗
                </a>
              )}
            </div>
          </motion.article>
        ))}
      </div>

      {visible.length === 0 && (
        <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500" data-testid="events-empty">
          Nothing on the books for this filter — check All.
        </p>
      )}
    </section>
  );
}
